import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import envConfig from '../config/envConfig';
import { LogOut, Phone, ChevronDown, MessageCircle, HelpCircle } from 'lucide-react';

// ─── Navbar ────────────────────────────────────────────────────
export function Navbar() {
  const { user, logout, toast } = useApp();
  const navigate = useNavigate();
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [customerCareNumbers, setCustomerCareNumbers] = useState([]);
  const userMenuRef = useRef(null);
  const inquiryMenuRef = useRef(null);

  // Load customer care numbers from API
  useEffect(() => {
    const loadCustomerCare = async () => {
      try {
        const response = await fetch(`${envConfig.apiBaseUrl}/content/customer-care`);
        const data = await response.json();
        if (data.success && data.data) {
          setCustomerCareNumbers(data.data);
        }
      } catch (err) {
        console.error('Failed to load customer care numbers:', err);
      }
    };
    loadCustomerCare();
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (inquiryMenuRef.current && !inquiryMenuRef.current.contains(e.target)) {
        setInquiryOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => { logout(); navigate('/'); toast('Logged out successfully'); setUserMenuOpen(false); };

  const handleFaqClick = () => {
    setInquiryOpen(false);
    const el = document.getElementById('faq');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#faq');
      setTimeout(() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' }), 300);
    }
  };

  // Role-based menu items
  const learnerMenuItems = [
    { icon: '🔍', label: 'Discover Experts', path: '/learner' },
    { icon: '📅', label: 'My Sessions',      path: '/learner?tab=sessions' },
    { icon: '⏱',  label: 'History',          path: '/learner?tab=history' },
    { icon: '👤', label: 'My Profile',       path: '/learner?tab=profile' },
  ];
  const expertMenuItems = [
    { icon: '📊', label: 'Dashboard',    path: '/expert' },
    { icon: '📅', label: 'My Calendar',  path: '/expert?tab=calendar' },
    { icon: '📋', label: 'Bookings',     path: '/expert?tab=bookings' },
    { icon: '👤', label: 'My Profile',   path: '/expert?tab=profile' },
    { icon: '💰', label: 'Earnings',     path: '/expert?tab=earnings' },
  ];
  const adminMenuItems = [
    { icon: '⚙️', label: 'Admin Panel', path: '/admin' },
  ];

  const menuItems = user?.role === 'expert' ? expertMenuItems
    : user?.role === 'admin' ? adminMenuItems
    : learnerMenuItems;

  const roleBadgeColor = user?.role === 'expert' ? 'var(--teal)'
    : user?.role === 'admin' ? '#C13030'
    : 'var(--saffron)';

  return (
    <nav style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border2)', position: 'sticky', top: 0, zIndex: 100, boxShadow: 'var(--shadow-sm)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', maxWidth: 1200, margin: '0 auto' }}>

        {/* Logo — always navigates to home */}
        <div className="flex items-center cursor-pointer" style={{ flexShrink: 0 }} onClick={() => navigate('/')}>
          <img src="/logo.png" alt="MakeMyExpert" style={{ height: 36, width: 'auto', objectFit: 'contain' }}
            onError={e => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div style={{ display: 'none', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 32, height: 32, background: 'linear-gradient(135deg, var(--saffron), var(--teal))', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: 'white', fontSize: 16 }}>✦</span>
            </div>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>MakeMyExpert</span>
          </div>
        </div>

        <div className="flex-1" />

        {user ? (
          /* ── Logged in: User avatar — click to go to dashboard ── */
          <div style={{ position: 'relative' }} ref={userMenuRef}>
            <div
              onClick={() => {
                const dashboardPath = user.role === 'expert' ? '/expert' 
                  : (user.role === 'learner' || user.role === 'receiver') ? '/learner'
                  : '/admin';
                navigate(dashboardPath);
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 10px', borderRadius: 999, border: '1px solid var(--border)', background: 'var(--surface)', transition: 'var(--transition)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}
            >
              <Avatar name={user.name} bg={user.bg} size="sm" />
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{user.name.split(' ')[0]}</span>
                <span style={{ fontSize: 10, fontWeight: 600, color: roleBadgeColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {user.role === 'learner' || user.role === 'receiver' ? 'Learner' : user.role === 'expert' ? 'Expert' : 'Admin'}
                </span>
              </div>
            </div>
          </div>
        ) : (
          /* ── Logged out: Login/Join buttons ── */
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={() => navigate('/auth?mode=login')} className="btn btn-secondary btn-sm">Log in</button>
            <button onClick={() => navigate('/auth?mode=register')} className="btn btn-primary btn-sm">Join us</button>
          </div>
        )}

        {/* Help dropdown */}
        <div style={{ position: 'relative' }} ref={inquiryMenuRef}>
          <button
            onClick={() => setInquiryOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 999, fontSize: 13, fontWeight: 600, color: 'var(--ink2)', cursor: 'pointer' }}
          >
            Help <ChevronDown size={12} style={{ transition: 'transform 0.2s', transform: inquiryOpen ? 'rotate(180deg)' : 'none' }} />
          </button>
          {inquiryOpen && (
            <div style={{ position: 'absolute', right: 0, top: '110%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow-lg)', minWidth: 240, maxHeight: 400, overflowY: 'auto', zIndex: 200, padding: 8, animation: 'fadeUp 0.15s ease' }}>
              {/* Dynamic contact numbers */}
              {customerCareNumbers.length > 0 ? (
                customerCareNumbers.map((cc) => (
                  <div key={cc.id}>
                    {/* Country header */}
                    {cc.country && (
                      <div style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: 'var(--ink4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {cc.country}
                      </div>
                    )}
                    {/* Phone */}
                    {cc.phone && (
                      <a href={`tel:${cc.phone.replace(/\D/g, '')}`} onClick={() => setInquiryOpen(false)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, textDecoration: 'none', color: 'var(--ink2)', fontSize: 13, fontWeight: 600 }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--teal-lt)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Phone size={13} style={{ color: 'var(--teal)' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: 10, color: 'var(--ink4)', fontWeight: 400 }}>Call</div>
                          {cc.phone}
                        </div>
                      </a>
                    )}
                    {/* WhatsApp */}
                    {cc.whatsapp && (
                      <a href={`https://wa.me/${cc.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" onClick={() => setInquiryOpen(false)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, textDecoration: 'none', color: 'var(--ink2)', fontSize: 13, fontWeight: 600 }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <MessageCircle size={13} style={{ color: '#16a34a' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: 10, color: 'var(--ink4)', fontWeight: 400 }}>WhatsApp</div>
                          {cc.whatsapp}
                        </div>
                      </a>
                    )}
                  </div>
                ))
              ) : null}
              {customerCareNumbers.length > 0 && (
                <div style={{ height: 1, background: 'var(--border2)', margin: '4px 8px' }} />
              )}
              {/* FAQ */}
              <div onClick={handleFaqClick}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, cursor: 'pointer', color: 'var(--ink2)', fontSize: 13, fontWeight: 600 }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--saffron-lt)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <HelpCircle size={13} style={{ color: 'var(--saffron)' }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--ink4)', fontWeight: 400 }}>Browse answers</div>
                  FAQ
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

// ─── Toast container ────────────────────────────────────────────
export function ToastContainer() {
  const { toasts } = useApp();
  return (
    <div className="toast-wrap">
      {toasts.map(t => (
        <div key={t.id} className={`toast ${t.type !== 'default' ? t.type : ''}`}>{t.msg}</div>
      ))}
    </div>
  );
}

// ─── Modal ─────────────────────────────────────────────────────
export function Modal({ open, onClose, title, subtitle, children, maxWidth = 520 }) {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-drag-handle" />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <div className="modal-title">{title}</div>
          <button onClick={onClose} style={{ fontSize: 22, color: 'var(--ink4)', lineHeight: 1, padding: '0 0 0 16px' }}>×</button>
        </div>
        {subtitle && <div className="modal-sub">{subtitle}</div>}
        {children}
      </div>
    </div>
  );
}

// ─── Avatar ─────────────────────────────────────────────────────
export function Avatar({ name, bg = '#0B6E65', size = 'md', src, style: extraStyle }) {
  const initials = name?.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase() || '?';
  const sizes  = { sm: 32, md: 44, lg: 64, xl: 88 };
  const fSizes = { sm: 12, md: 16, lg: 22, xl: 30 };
  const px = sizes[size] || 44;
  const base = { width: px, height: px, borderRadius: '50%', flexShrink: 0, ...extraStyle };

  if (src) {
    return (
      <div style={{ ...base, overflow: 'hidden' }}>
        <img src={src} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    );
  }
  return (
    <div style={{ ...base, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: fSizes[size], fontWeight: 700, fontFamily: 'var(--font-display)' }}>
      {initials}
    </div>
  );
}

// ─── Badge ──────────────────────────────────────────────────────
export function Badge({ children, variant = 'gray' }) {
  return <span className={`badge badge-${variant}`}>{children}</span>;
}

// ─── Stars ──────────────────────────────────────────────────────
export function Stars({ rating }) {
  return (
    <span className="stars" style={{ fontSize: 13 }}>
      {'★'.repeat(Math.floor(rating))}{'☆'.repeat(5 - Math.floor(rating))}
    </span>
  );
}

// ─── Stat Card ──────────────────────────────────────────────────
export function StatCard({ label, value, delta, accent }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={accent ? { color: accent } : {}}>{value}</div>
      {delta && <div className="stat-delta">{delta}</div>}
    </div>
  );
}

// ─── Mini Calendar ──────────────────────────────────────────────
export function MiniCalendar({ year, month, onNavigate, renderDay, onSelectDay, dimEmpty = false }) {
  const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const monthLabel = new Date(year, month).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} className="cal-day other-month" />);
  for (let d = 1; d <= daysInMonth; d++) {
    const isPast      = new Date(year, month, d) < todayMidnight;
    const isToday     = d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
    const extra       = renderDay ? renderDay(d) : {};
    const isEmpty     = dimEmpty && !extra.hasSlots;
    const isDisabled  = isPast || isEmpty;

    let dayStyle = {};
    if (isPast)             dayStyle = { pointerEvents: 'none', opacity: 0.38 };
    else if (isEmpty)       dayStyle = { pointerEvents: 'none', opacity: 0.35 };
    else if (extra.hasSlots) dayStyle = { background: '#d1fae5', borderRadius: 6 };

    cells.push(
      <div key={d}
        className={`cal-day ${isToday ? 'today' : ''} ${isDisabled ? 'other-month' : ''} ${extra.selected ? 'selected' : ''}`}
        onClick={() => !isDisabled && onSelectDay?.(d)}
        style={dayStyle}
      >
        <div className="cal-day-num">{d}</div>
        {!isDisabled && extra.dots}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-12 mb-12">
        <button onClick={() => onNavigate(-1)} className="btn btn-secondary btn-sm" style={{ padding: '4px 10px' }}>‹</button>
        <span style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 14 }}>{monthLabel}</span>
        <button onClick={() => onNavigate(1)}  className="btn btn-secondary btn-sm" style={{ padding: '4px 10px' }}>›</button>
      </div>
      <div className="cal-grid-head">{DAYS.map(d => <span key={d}>{d}</span>)}</div>
      <div className="cal-grid-days">{cells}</div>
    </div>
  );
}

// ─── Section Header ─────────────────────────────────────────────
export function SectionHeader({ eyebrow, title, subtitle, right }) {
  return (
    <div className="flex items-center justify-between mb-20">
      <div>
        {eyebrow && <div className="eyebrow mb-4">{eyebrow}</div>}
        {title && <h2 style={{ fontSize: 22, fontWeight: 700 }}>{title}</h2>}
        {subtitle && <p className="text-sm text-ink3 mt-4">{subtitle}</p>}
      </div>
      {right && <div>{right}</div>}
    </div>
  );
}

// ─── Booking status badge ───────────────────────────────────────
export function BookingBadge({ status }) {
  const map = { pending: 'pending', confirmed: 'confirmed', completed: 'completed', declined: 'declined' };
  return <span className={`badge badge-${map[status] || 'gray'}`}>{status}</span>;
}

// ─── Empty State ─────────────────────────────────────────────────
export function EmptyState({ icon = '📭', title, description, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--ink3)' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--ink2)', marginBottom: 6 }}>{title}</div>
      {description && <div style={{ fontSize: 13, marginBottom: 16 }}>{description}</div>}
      {action}
    </div>
  );
}

// ─── Toggle ──────────────────────────────────────────────────────
export function Toggle({ checked, onChange }) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span className="toggle-slider" />
    </label>
  );
}

// ─── Slot Pill (module-level to avoid remount on every ExpertCard render) ──
function SlotPill({ type, slots, active, onEnter, onLeave }) {
  const isSolo  = type === 'solo';
  const label   = isSolo ? '👤 1:1 Slots' : '🎓 Group Batches';
  const color   = isSolo ? 'var(--sage)' : 'var(--teal)';
  const bgColor = isSolo ? 'rgba(74,124,89,0.08)' : 'rgba(11,110,101,0.08)';
  const border  = isSolo ? '1px solid rgba(74,124,89,0.25)' : '1px solid rgba(11,110,101,0.25)';
  const pillRef = useRef(null);
  const [tooltipStyle, setTooltipStyle] = useState({});

  const handleMouseEnter = (e) => {
    e.stopPropagation();
    if (pillRef.current) {
      const rect = pillRef.current.getBoundingClientRect();
      const tooltipWidth = 260;
      let left = rect.left + rect.width / 2 - tooltipWidth / 2;
      // Keep within viewport
      if (left < 8) left = 8;
      if (left + tooltipWidth > window.innerWidth - 8) left = window.innerWidth - tooltipWidth - 8;
      setTooltipStyle({
        position: 'fixed',
        top: rect.top - 10, // will be adjusted with transform
        left,
        width: tooltipWidth,
        transform: 'translateY(-100%)',
        zIndex: 9999,
      });
    }
    onEnter();
  };

  return (
    <div
      ref={pillRef}
      style={{ flex: 1, background: bgColor, border, borderRadius: 8, padding: '8px 10px', cursor: 'default' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onLeave}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
      {slots.length === 0
        ? <div style={{ fontSize: 11, color: 'var(--ink4)' }}>None available</div>
        : <div style={{ fontSize: 12, fontWeight: 700, color }}>
            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: color, marginRight: 4, verticalAlign: 'middle' }} />
            {isSolo ? `${slots.length} open` : `${slots.length} slot${slots.length !== 1 ? 's' : ''}`}
          </div>
      }

      {active && slots.length > 0 && ReactDOM.createPortal(
        <div
          onClick={e => e.stopPropagation()}
          style={{
            ...tooltipStyle,
            background: '#1C1611',
            borderRadius: 12,
            padding: '12px 14px',
            boxShadow: '0 8px 28px rgba(0,0,0,0.32)',
            border: '1px solid rgba(255,255,255,0.08)',
            pointerEvents: 'none',
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 700, color: isSolo ? '#F4A84A' : '#5DD4CB', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
            {isSolo ? '1:1 Available Slots' : 'Group Batch Slots'}
          </div>
          {slots.slice(0, 5).map((s, i) => {
            const dateStr = new Date(s.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
            const bookedCount = s.booked_count || 0;
            const maxJoinees = s.max_joinees || 1;
            const minJoinees = s.min_joinees || 1;
            return (
              <div key={s.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14,
                paddingBottom: i < Math.min(slots.length, 5) - 1 ? 8 : 0,
                marginBottom: i < Math.min(slots.length, 5) - 1 ? 8 : 0,
                borderBottom: i < Math.min(slots.length, 5) - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#F7F4EF' }}>{dateStr}</div>
                  <div style={{ fontSize: 11, color: 'rgba(247,244,239,0.5)', marginTop: 2 }}>{s.start} – {s.end}</div>
                  {!isSolo && (
                    <div style={{ fontSize: 10, color: 'rgba(247,244,239,0.6)', marginTop: 3 }}>
                      Enrolled: <span style={{ fontWeight: 600, color: '#5DD4CB' }}>{bookedCount}/{maxJoinees}</span> • Min: {minJoinees}
                    </div>
                  )}
                </div>
                {s.price > 0 && (
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#F4A84A', flexShrink: 0 }}>₹{Number(s.price).toLocaleString()}</div>
                )}
              </div>
            );
          })}
          {slots.length > 5 && <div style={{ fontSize: 11, color: 'rgba(247,244,239,0.4)', marginTop: 8 }}>+{slots.length - 5} more slots</div>}
          {/* Caret */}
          <div style={{ position: 'absolute', bottom: -5, left: '50%', marginLeft: -5, width: 10, height: 10, background: '#1C1611', border: '1px solid rgba(255,255,255,0.08)', borderTop: 'none', borderLeft: 'none', transform: 'rotate(45deg)' }} />
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Expert Card ───────────────────────────────────────────────
export function ServiceCard({ service, tags = [], onClick }) {
  const tagNames = (service.tag_ids || []).map(id => tags.find(t => t.id === id || t.id === Number(id))?.name).filter(Boolean);
  const subcats = service.subcategories || [];

  return (
    <div
      className="card"
      onClick={onClick}
      style={{ cursor: 'pointer', borderRadius: 14, overflow: 'hidden', transition: 'box-shadow 0.18s, transform 0.18s', display: 'flex', flexDirection: 'column' }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = ''; }}
    >
      {/* Category banner */}
      <div style={{ background: 'var(--teal, #0B6E65)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 18 }}>{service.category_icon || '📚'}</span>
        <span style={{ color: 'white', fontSize: 12, fontWeight: 600 }}>{service.category_name || 'Service'}</span>
      </div>

      <div style={{ padding: '14px 16px', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Expert info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar src={service.expert_photo} name={service.expert_name} size="sm" />
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink2)' }}>{service.expert_name}</div>
        </div>

        {/* Title + desc */}
        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink)', lineHeight: 1.3 }}>{service.title}</div>
        {service.description && (
          <div style={{ fontSize: 12, color: 'var(--ink4)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {service.description}
          </div>
        )}

        {/* Subcategories */}
        {subcats.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {subcats.map(s => (
              <span key={s} style={{ background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 6, padding: '2px 8px', fontSize: 11, color: 'var(--ink3)' }}>{s}</span>
            ))}
          </div>
        )}

        {/* Tags */}
        {tagNames.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {tagNames.map(t => (
              <span key={t} style={{ background: '#E3F4F2', color: '#0B6E65', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>🏷️ {t}</span>
            ))}
          </div>
        )}

        {/* Price + duration + availability + book */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 10, borderTop: '1px solid var(--border2)' }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--saffron)' }}>₹{Number(service.price).toLocaleString()}</div>
            <div style={{ fontSize: 11, color: 'var(--ink4)' }}>{service.duration}</div>
            {Number(service.available_slots) > 0
              ? <div style={{ fontSize: 11, color: '#0B6E65', fontWeight: 600, marginTop: 2 }}>📅 {service.available_slots} slot{service.available_slots !== 1 ? 's' : ''} available</div>
              : <div style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 2 }}>No open slots</div>
            }
          </div>
          <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); onClick && onClick(); }}>
            Book Session →
          </button>
        </div>
      </div>
    </div>
  );
}

export function ExpertCard({ expert, onClick }) {
  const p = expert;
  const [showTooltip, setShowTooltip] = useState(null); // 'solo' | 'group' | null

  const soloOpen  = (p.slots || []).filter(s => s.type !== 'group');
  const groupOpen = (p.slots || []).filter(s => s.type === 'group');

  // Minimum price from actual available slots; fall back to min service price
  const allSlotPrices = (p.slots || []).map(s => Number(s.price)).filter(n => n > 0);
  const displayPrice = allSlotPrices.length > 0
    ? Math.min(...allSlotPrices)
    : (p.rate || 0);

  // 2.5× the original md size (44px)
  const PHOTO_SIZE = 110;

  return (
    <div
      onClick={onClick}
      className="card card-hover cursor-pointer"
      style={{ position: 'relative', overflow: 'visible' }}
    >
      {/* Coloured banner */}
      <div style={{
        height: 88,
        background: `linear-gradient(135deg, ${p.bg}55, ${p.bg}99)`,
        borderRadius: '12px 12px 0 0',
        position: 'relative',
        overflow: 'hidden',
      }} />

      {/* Large centred profile photo — sits on top of banner */}
      <div style={{
        position: 'absolute',
        top: 88 - PHOTO_SIZE / 2,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2,
      }}>
        <Avatar
          name={p.name}
          bg={p.bg}
          src={p.photo || p.profile_image}
          style={{
            width: PHOTO_SIZE,
            height: PHOTO_SIZE,
            fontSize: 38,
            border: '4px solid var(--surface)',
            boxShadow: '0 4px 18px rgba(0,0,0,0.13)',
          }}
        />
      </div>

      {/* Card body */}
      <div style={{ padding: `${PHOTO_SIZE / 2 + 14}px 16px 16px`, textAlign: 'center' }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>{p.name}</div>
        <div style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 2 }}>
          {p.catName || p.category_name || ''}
        </div>
        {(p.subcategories || []).length > 0 && (
          <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 4 }}>
            {(p.subcategories || []).slice(0, 3).join(' | ')}
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8, justifyContent: 'center' }}>
          {p.tags.slice(0, 2).map(t => <span key={t} className="badge badge-teal">{t}</span>)}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border2)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--saffron)' }}>
            {displayPrice > 0 ? `from ₹${displayPrice.toLocaleString()}` : 'Free intro'}
          </div>
          <div style={{ textAlign: 'right' }}>
            {p.rating > 0
              ? <>
                  <Stars rating={p.rating} />
                  <div style={{ fontSize: 11, color: 'var(--ink4)' }}>{p.rating} · {p.sessions} sessions</div>
                </>
              : null
            }
          </div>
        </div>

        {/* Slot groups */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <SlotPill
            type="solo" slots={soloOpen}
            active={showTooltip === 'solo'}
            onEnter={() => setShowTooltip('solo')}
            onLeave={() => setShowTooltip(null)}
          />
          <SlotPill
            type="group" slots={groupOpen}
            active={showTooltip === 'group'}
            onEnter={() => setShowTooltip('group')}
            onLeave={() => setShowTooltip(null)}
          />
        </div>

        {/* Book button */}
        <button
          onClick={e => { e.stopPropagation(); onClick && onClick(); }}
          className="btn btn-primary"
          style={{ width: '100%', justifyContent: 'center', marginTop: 10, borderRadius: 8, padding: '11px 16px', fontSize: 13 }}
        >
          Book a Session →
        </button>
      </div>
    </div>
  );
}
